import React, { useState } from 'react';
import { ChevronRight, ChevronLeft, Sparkles, Gift, Download, Wine, Check } from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';
import './Survey.css';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// ============================================
// SURVEY QUESTIONS - Vintage & Venture Edition
// ============================================
const surveyQuestions = [
  {
    id: 1,
    question: "Which best describes your primary role today?",
    type: "single",
    required: true,
    options: [
      "Startup Founder / Co-Founder",
      "Venture Capital / Angel Investor",
      "CEO / C-Suite (non-startup)",
      "Operator / Executive (VP, Director, Head of X)",
      "Advisor / Board Member",
      "Other"
    ]
  },
  {
    id: 2,
    question: "In the next 6–12 months, what are you most actively looking for?",
    type: "multiple",
    required: false,
    options: [
      "New customers or distribution channels",
      "Strategic partnerships",
      "Trusted service providers (legal, marketing, dev, finance, ops, etc.)",
      "Investment opportunities",
      "Capital for my company",
      "Talent / contractors / fractional executives"
    ]
  },
  {
    id: 3,
    question: "How do you currently discover or vet products and services for your business?",
    type: "multiple",
    required: true,
    options: [
      "Personal network / referrals",
      "LinkedIn or social media",
      "Online marketplaces or platforms",
      "Cold outreach (email, DMs, events)",
      "Agencies or consultants",
      "I don't have a reliable system"
    ]
  },
  {
    id: 4,
    question: "What would make a marketplace app genuinely valuable enough for you to use regularly?",
    type: "multiple",
    required: true,
    options: [
      "Curated, high-quality vendors (not mass listings)",
      "Verified users and businesses",
      "Warm introductions or mutual connections",
      "Ability to both buy and sell services/products",
      "Time savings vs. manual networking",
      "Founder / investor-only access or filters"
    ]
  },
  {
    id: 5,
    question: "Would you like early or exclusive access to bHyyve?",
    type: "single",
    required: true,
    options: [
      "Yes — I'll download it now",
      "Yes — send me access details",
      "Maybe later",
      "No, just here for the wine 🍷"
    ]
  }
];

// Gift options
const giftOptions = [
  { 
    name: "Free Service Credit", 
    description: "$25 off your first booking",
    code: "GYYFTS-CREDIT-25", 
    color: "#10B981" 
  },
  { 
    name: "Referral Bonus", 
    description: "$10 for you and a friend",
    code: "GYYFTS-REFER-10", 
    color: "#3B82F6" 
  },
  { 
    name: "Referral Bonus", 
    description: "$20 for you and a friend",
    code: "GYYFTS-REFER-20", 
    color: "#8B5CF6" 
  },
  { 
    name: "Free classic brownies at Suggaholic!", 
    description: "1 for you and 1 for a friend =) (same visit)",
    code: "GYYFTS-BROWNIE-2", 
    color: "#8B4513" 
  }
];

const Survey = () => {
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState({});
  const [surveyComplete, setSurveyComplete] = useState(false);
  const [showAnimation, setShowAnimation] = useState(false);
  const [showGift, setShowGift] = useState(false);
  const [selectedGift, setSelectedGift] = useState(null);
  const [giftClaimed, setGiftClaimed] = useState(false);
  const [contactInfo, setContactInfo] = useState('');
  const [showContactCapture, setShowContactCapture] = useState(false);
  const [thankYouType, setThankYouType] = useState(null); // 'download' or 'passive'
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Contact form fields
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: ''
  });

  const progress = ((currentQuestion + 1) / surveyQuestions.length) * 100;
  const question = surveyQuestions[currentQuestion];
  const currentAnswer = answers[question?.id];
  
  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  const isFormValid = () => {
    return formData.name.trim() && formData.email.trim() && formData.email.includes('@');
  };

  const handleSingleAnswer = (answer) => {
    setAnswers(prev => ({
      ...prev,
      [question.id]: answer
    }));
  };

  const handleMultipleAnswer = (answer) => {
    const currentAnswers = answers[question.id] || [];
    const newAnswers = currentAnswers.includes(answer)
      ? currentAnswers.filter(a => a !== answer)
      : [...currentAnswers, answer];
    
    setAnswers(prev => ({
      ...prev,
      [question.id]: newAnswers
    }));
  };

  const isAnswerSelected = (option) => {
    if (question.type === 'multiple') {
      return (currentAnswer || []).includes(option);
    }
    return currentAnswer === option;
  };

  const canProceed = () => {
    if (!question.required) return true;
    if (question.type === 'multiple') {
      return (currentAnswer || []).length > 0;
    }
    return !!currentAnswer;
  };

  const handleNext = () => {
    if (currentQuestion < surveyQuestions.length - 1) {
      setCurrentQuestion(prev => prev + 1);
    } else {
      // Survey complete - check logic jump
      const lastAnswer = answers[5];
      
      if (lastAnswer === "Yes — I'll download it now") {
        // Jump to download screen
        setSurveyComplete(true);
        setShowAnimation(true);
        setTimeout(() => {
          const randomGift = giftOptions[Math.floor(Math.random() * giftOptions.length)];
          setSelectedGift(randomGift);
          setShowGift(true);
          setThankYouType('download');
        }, 4000);
      } else if (lastAnswer === "Yes — send me access details") {
        // Show contact capture
        setShowContactCapture(true);
      } else {
        // Passive close
        setSurveyComplete(true);
        setThankYouType('passive');
      }
    }
  };

  const handlePrev = () => {
    if (currentQuestion > 0) {
      setCurrentQuestion(prev => prev - 1);
    }
  };

  const handleContactSubmit = async () => {
    if (!contactInfo.trim()) {
      toast.error('Please enter your email or LinkedIn');
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await fetch(`${API_URL}/api/survey-submit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: contactInfo.includes('@') ? contactInfo : `linkedin:${contactInfo}`,
          answers,
          gift: { name: "Early Access", code: "EARLYBIRD", color: "#fbbf24" },
          isVendor: answers[1] === "Startup Founder / Co-Founder" || answers[1] === "CEO / C-Suite (non-startup)"
        }),
      });

      if (response.ok) {
        setShowContactCapture(false);
        setSurveyComplete(true);
        setThankYouType('passive');
        toast.success('Thanks! We\'ll be in touch soon.');
      } else {
        toast.error('Something went wrong. Please try again.');
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Failed to submit. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClaimGift = async () => {
    if (!isFormValid()) {
      toast.error('Please enter your name and email address');
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await fetch(`${API_URL}/api/survey-submit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: formData.email,
          name: formData.name,
          phone: formData.phone,
          answers,
          gift: selectedGift,
          isVendor: answers[1] === "Startup Founder / Co-Founder" || answers[1] === "CEO / C-Suite (non-startup)"
        }),
      });

      if (response.ok) {
        setGiftClaimed(true);
        toast.success('Gift claimed! Check your email for details.');
      } else {
        toast.error('Failed to submit. Please try again.');
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Something went wrong.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Contact Capture Screen
  if (showContactCapture) {
    return (
      <div className="survey-page">
        <div className="survey-container">
          <div className="question-card contact-card">
            <div className="contact-icon">📬</div>
            <h2 className="question-text">Where should we send your access?</h2>
            <p className="contact-description">Email or LinkedIn works.</p>
            
            <input
              type="text"
              value={contactInfo}
              onChange={(e) => setContactInfo(e.target.value)}
              placeholder="email@company.com or linkedin.com/in/you"
              className="contact-input"
              autoFocus
            />
            
            <Button
              onClick={handleContactSubmit}
              disabled={isSubmitting || !contactInfo.trim()}
              className="submit-button"
            >
              {isSubmitting ? 'Sending...' : 'Send Me Access'}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // Thank You Screen B: Passive Close
  if (surveyComplete && thankYouType === 'passive') {
    return (
      <div className="survey-page thank-you-page">
        <div className="thank-you-container">
          <div className="thank-you-icon">
            <Wine size={64} className="text-purple-400" />
          </div>
          <h1 className="thank-you-title">Thanks for sharing.</h1>
          <p className="thank-you-description">
            We'll be in touch soon. Enjoy the rest of Vintage & Venture 🍷
          </p>
          <a href="/" className="back-home-link">
            Back to bHyyve
          </a>
        </div>
      </div>
    );
  }

  // Animation and Gift View (Thank You Screen A: Download)
  if (surveyComplete && (showAnimation || showGift)) {
    return (
      <div className="survey-page gift-reveal-page">
        {/* Bee Animation Scene */}
        {showAnimation && !showGift && (
          <div className="animation-scene">
            <div className="scene-text">
              <Sparkles className="inline text-yellow-400 animate-pulse" size={28} />
              <span>Our bee is finding your exclusive gift...</span>
              <Sparkles className="inline text-yellow-400 animate-pulse" size={28} />
            </div>
            
            {/* Flower Garden */}
            <div className="gift-flower-garden">
              {[...Array(6)].map((_, i) => (
                <div key={i} className={`gift-flower gift-flower-${i + 1}`}>
                  <div className="gift-flower-center">🎁</div>
                  <div className="gift-petal gift-petal-1"></div>
                  <div className="gift-petal gift-petal-2"></div>
                  <div className="gift-petal gift-petal-3"></div>
                  <div className="gift-petal gift-petal-4"></div>
                  <div className="gift-petal gift-petal-5"></div>
                  <div className="gift-petal gift-petal-6"></div>
                </div>
              ))}
            </div>

            {/* Animated Bee */}
            <div className="delivery-bee">
              <div className="survey-bee-body">
                <div className="survey-bee-wing survey-bee-wing-left"></div>
                <div className="survey-bee-wing survey-bee-wing-right"></div>
                <div className="survey-bee-stripes">
                  <div className="survey-bee-stripe"></div>
                  <div className="survey-bee-stripe"></div>
                  <div className="survey-bee-stripe"></div>
                </div>
                <div className="survey-bee-face">
                  <div className="survey-bee-eye"></div>
                  <div className="survey-bee-eye"></div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Gift Reveal - Thank You Screen A */}
        {showGift && selectedGift && (
          <div className="gift-reveal-container">
            {/* Sparkler Effects */}
            <div className="sparkler-container">
              {[...Array(20)].map((_, i) => (
                <div key={i} className={`sparkler-particle sparkler-${i + 1}`}>✨</div>
              ))}
            </div>

            {/* Unfolding Gift Flower */}
            <div className={`gift-flower-reveal ${giftClaimed ? 'claimed' : ''}`}>
              <div className="gift-petals-container">
                <div className="gift-reveal-petal petal-reveal-1" style={{ backgroundColor: selectedGift.color }}></div>
                <div className="gift-reveal-petal petal-reveal-2" style={{ backgroundColor: selectedGift.color }}></div>
                <div className="gift-reveal-petal petal-reveal-3" style={{ backgroundColor: selectedGift.color }}></div>
                <div className="gift-reveal-petal petal-reveal-4" style={{ backgroundColor: selectedGift.color }}></div>
                <div className="gift-reveal-petal petal-reveal-5" style={{ backgroundColor: selectedGift.color }}></div>
                <div className="gift-reveal-petal petal-reveal-6" style={{ backgroundColor: selectedGift.color }}></div>
              </div>
              
              <div className="gift-center-reveal">
                <Gift size={48} className="gift-icon" />
              </div>
            </div>

            {/* Gift Details */}
            <div className="gift-details">
              <h1 className="gift-headline">You're in.</h1>
              
              <div className="gift-name-card" style={{ borderColor: selectedGift.color }}>
                <span className="gift-name">{selectedGift.name}</span>
                <span className="gift-description-text">{selectedGift.description}</span>
                <span className="gift-code">Your Code: {selectedGift.code}</span>
              </div>

              {!giftClaimed ? (
                <div className="claim-section">
                  <p className="claim-instruction">Enter your details to claim your gift:</p>
                  
                  <div className="claim-form">
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleFormChange}
                      placeholder="Full Name *"
                      className="claim-input"
                    />
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleFormChange}
                      placeholder="Email Address *"
                      className="claim-input"
                    />
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleFormChange}
                      placeholder="Phone Number (optional)"
                      className="claim-input"
                    />
                  </div>
                  
                  <Button
                    onClick={handleClaimGift}
                    disabled={isSubmitting || !isFormValid()}
                    className="claim-button"
                  >
                    {isSubmitting ? 'Claiming...' : 'Claim & Download'}
                  </Button>
                </div>
              ) : (
                <div className="download-section">
                  <p className="download-instruction">
                    Download bHyyve now to use your exclusive access!
                  </p>
                  <div className="download-buttons">
                    <a
                      href="https://play.google.com/store/apps/details?id=com.customers.bhyyve"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="download-button android"
                    >
                      <Download size={20} />
                      <span>Google Play</span>
                    </a>
                    <a
                      href="https://apps.apple.com/app/bhyyve"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="download-button ios"
                    >
                      <Download size={20} />
                      <span>App Store</span>
                    </a>
                  </div>
                </div>
              )}
            </div>

            {/* Floating Bee */}
            <div className="bee-with-gift">
              <div className="mini-bee">🐝</div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // Survey Questions View
  return (
    <div className="survey-page">
      <div className="survey-container">
        {/* Header */}
        <div className="survey-header">
          <h1 className="survey-title">
            <Wine className="inline text-purple-400" size={32} />
            Vintage & Venture
            <Sparkles className="inline text-yellow-400" size={32} />
          </h1>
          <p className="survey-subtitle">Quick questions to personalize your bHyyve experience</p>
        </div>

        {/* Progress Bar */}
        <div className="progress-container">
          <div className="progress-bar">
            <div 
              className="progress-fill"
              style={{ width: `${progress}%` }}
            ></div>
          </div>
          <p className="progress-text">
            {currentQuestion + 1} of {surveyQuestions.length}
            {question.type === 'multiple' && <span className="select-hint"> • Select all that apply</span>}
          </p>
        </div>

        {/* Question Card */}
        <div className="question-card">
          <h2 className="question-text">{question.question}</h2>
          
          <div className="options-container">
            {question.options.map((option, index) => (
              <button
                key={index}
                className={`option-button ${isAnswerSelected(option) ? 'selected' : ''} ${question.type === 'multiple' ? 'multi' : ''}`}
                onClick={() => question.type === 'multiple' ? handleMultipleAnswer(option) : handleSingleAnswer(option)}
              >
                <span className={`option-indicator ${question.type === 'multiple' ? 'checkbox' : 'radio'}`}>
                  {isAnswerSelected(option) && <Check size={14} />}
                </span>
                <span className="option-text">{option}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Navigation */}
        <div className="survey-navigation">
          <Button
            onClick={handlePrev}
            disabled={currentQuestion === 0}
            className="nav-button prev-button"
          >
            <ChevronLeft size={20} />
            Back
          </Button>
          
          <Button
            onClick={handleNext}
            disabled={!canProceed()}
            className="nav-button next-button"
          >
            {currentQuestion === surveyQuestions.length - 1 ? 'Finish' : 'Continue'}
            <ChevronRight size={20} />
          </Button>
        </div>
      </div>

      {/* Decorative Flowers */}
      <div className="flower-garden">
        {[...Array(8)].map((_, i) => (
          <div key={i} className={`flower flower-${i + 1}`}>
            <div className="flower-center">🎁</div>
            <div className="petal petal-1"></div>
            <div className="petal petal-2"></div>
            <div className="petal petal-3"></div>
            <div className="petal petal-4"></div>
            <div className="petal petal-5"></div>
            <div className="petal petal-6"></div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default Survey;
